import { Timer } from "lucide-react";
import { useEffect, useRef } from "react";

import { useNow } from "@/hooks/use-now";
import { formatClock, formatEasternTimestamp } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * The countdown to a challenge's opening time. `card` is the line on the challenge list;
 * `banner` sits at the top of the challenge page. `onOpen` fires once, when the clock
 * reaches zero, so the page can reload into the open challenge.
 */
export function OpensCountdown({
  opensAt,
  variant,
  onOpen,
  className,
}: {
  opensAt: number;
  variant: "card" | "banner";
  onOpen?: () => void;
  className?: string;
}) {
  const now = useNow(1000);
  const left = opensAt - now;
  const fired = useRef(false);

  useEffect(() => {
    if (left <= 0 && onOpen && !fired.current) {
      fired.current = true;
      onOpen();
    }
  }, [left, onOpen]);

  if (left <= 0) return null;

  if (variant === "card") {
    return (
      <p className={cn("text-primary flex items-center gap-1.5 font-medium", className)}>
        <Timer className="size-4" aria-hidden />
        Opens in <span className="font-mono font-bold tabular-nums">{formatClock(left)}</span>
      </p>
    );
  }

  return (
    <div
      role="timer"
      className={cn(
        "border-primary/30 bg-primary/5 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border px-4 py-3",
        className,
      )}
    >
      <Timer className="text-primary size-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium">Opens {formatEasternTimestamp(opensAt)}</p>
        <p className="text-muted-foreground text-sm">
          The exploration data and runs unlock then, for everyone at once. This page
          refreshes itself when it opens.
        </p>
      </div>
      <span className="text-primary font-mono text-2xl font-bold tabular-nums">
        {formatClock(left)}
      </span>
    </div>
  );
}
